import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { openCartDrawer } from "../../store/slices/ui-slice";
import { useGetUserProfileQuery } from "../../store/slices/user-slice";
import { useGetCategoryQuery } from "../../store/slices/orderSlice";
import { buildCategoryOptions } from "../../config/shop-filters";
import {
  HomeIcon,
  CategoryIcon,
  ShopIcon,
  CartIcon,
  ProfileIcon,
  CloseIcon,
} from "./icons";

const SHOP = "/shop-grid-standard";

/*
 * App-style bottom navigation, shown only on phone/tablet widths.
 *
 * Items are adapted to the screens this app actually has: Cart opens the
 * existing cart drawer rather than navigating away, and Category opens a
 * sheet built from the real /categories response, so nothing here is
 * static or mocked. Everything else is ordinary routing.
 */
const MobileBottomNav = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [sheetOpen, setSheetOpen] = useState(false);

  const { cartItems } = useSelector((state) => state.cart);
  const { data: profile } = useGetUserProfileQuery();
  const { data: categories, isLoading: categoriesLoading } = useGetCategoryQuery();

  const isLoggedIn = !!profile?.user;
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Only categories the API actually returned can be filtered on.
  const categoryOptions = buildCategoryOptions(categories || []).filter(
    (option) => option.value
  );

  const isHome = pathname === "/" || pathname === "/home";
  const isShop = pathname.startsWith("/shop");
  const profilePath = isLoggedIn ? "/profile" : "/login-register";
  const isProfile =
    pathname.startsWith("/profile") ||
    pathname.startsWith("/my-account") ||
    pathname.startsWith("/login-register");

  // Close the sheet whenever a route change happens under it.
  useEffect(() => {
    setSheetOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!sheetOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setSheetOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheetOpen]);

  const pickCategory = (option) => {
    setSheetOpen(false);
    // The shop page seeds its category filter from this navigation state;
    // its own filtering logic is untouched.
    navigate(SHOP, { state: { category: option.value } });
  };

  return (
    <>
      {/* Category sheet */}
      <div
        className={`c4mnav-sheet${sheetOpen ? " is-open" : ""}`}
        aria-hidden={!sheetOpen}
      >
        <div
          className="c4mnav-sheet__scrim"
          onClick={() => setSheetOpen(false)}
        />
        <div
          className="c4mnav-sheet__panel"
          role="dialog"
          aria-modal="true"
          aria-label="Shop by category"
        >
          <div className="c4mnav-sheet__grip" aria-hidden="true" />
          <div className="c4mnav-sheet__head">
            <h2>Shop by Category</h2>
            <button
              type="button"
              onClick={() => setSheetOpen(false)}
              aria-label="Close categories"
              tabIndex={sheetOpen ? 0 : -1}
            >
              <CloseIcon />
            </button>
          </div>

          {categoriesLoading ? (
            <p className="c4mnav-sheet__empty">Loading categories…</p>
          ) : categoryOptions.length ? (
            <ul className="c4mnav-sheet__list">
              {categoryOptions.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    onClick={() => pickCategory(option)}
                    tabIndex={sheetOpen ? 0 : -1}
                  >
                    {option.label}
                    <span aria-hidden="true">›</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="c4mnav-sheet__empty">No categories available.</p>
          )}

          <Link
            className="c4mnav-sheet__all"
            to={SHOP}
            tabIndex={sheetOpen ? 0 : -1}
          >
            Browse all products
          </Link>
        </div>
      </div>

      <nav className="c4mnav" aria-label="Primary">
        <Link
          className={`c4mnav__item${isHome ? " is-active" : ""}`}
          to="/"
          aria-current={isHome ? "page" : undefined}
        >
          <HomeIcon />
          <span>Home</span>
        </Link>

        <button
          type="button"
          className={`c4mnav__item${sheetOpen ? " is-active" : ""}`}
          onClick={() => setSheetOpen((open) => !open)}
          aria-expanded={sheetOpen}
        >
          <CategoryIcon />
          <span>Category</span>
        </button>

        {/* Raised centre action, as in the reference */}
        <Link
          className={`c4mnav__fab${isShop ? " is-active" : ""}`}
          to={SHOP}
          aria-label="Shop"
          aria-current={isShop ? "page" : undefined}
        >
          <ShopIcon />
        </Link>

        <button
          type="button"
          className="c4mnav__item"
          onClick={() => dispatch(openCartDrawer())}
          aria-label={`Cart, ${cartCount} item${cartCount === 1 ? "" : "s"}`}
        >
          <span className="c4mnav__iconwrap">
            <CartIcon />
            {cartCount > 0 && (
              <span className="c4mnav__badge">{cartCount > 99 ? "99+" : cartCount}</span>
            )}
          </span>
          <span>Cart</span>
        </button>

        <Link
          className={`c4mnav__item${isProfile ? " is-active" : ""}`}
          to={profilePath}
          aria-current={isProfile ? "page" : undefined}
        >
          <ProfileIcon />
          <span>Profile</span>
        </Link>
      </nav>
    </>
  );
};

export default MobileBottomNav;
